import Vue from 'vue';

import {
  activateStation,
  blockCourse,
  deactivateCourseConnectionLocation,
  delayDeparture,
  getCourseConnectionsLocations,
  getCourses,
  getNextCourses,
  getStations,
  releaseCourse,
} from '@/services/courses';

export const state = {
  index: {},
  stations: {},
  courseConnectionsLocations: {},
  nextCourses: {},
  selectedCourses: {},
  lastRefresh: new Date(),
  courseConnectionLocationActivated: false,
  selectedStationFilter: null,
};

export const mutations = {
  setCourses(state, courses) {
    Vue.set(state, 'courses', {});
    courses.forEach(course => {
      Vue.set(state.index, course.id, course);
    });
  },
  setStations(state, stations) {
    Vue.set(state, 'stations', {});
    stations.forEach(station => {
      Vue.set(state.stations, station.sloid, station);
    });
  },
  setCourseConnectionsLocations(state, courseConnectionsLocations) {
    Vue.set(state, 'courseConnectionsLocations', {});
    courseConnectionsLocations.forEach(location => {
      Vue.set(state.courseConnectionsLocations, location.id, location);
    });
  },
  activateStation(state, station) {
    Vue.set(state.courseConnectionsLocations, station.id, station);
  },
  deactivateStation(state, id) {
    Vue.delete(state.courseConnectionsLocations, id);
  },
  setNextCourses(state, courses) {
    Vue.set(state, 'nextCourses', {});
    courses.forEach(course => {
      Vue.set(state.nextCourses, course.id, course);
    });
  },
  setSelectedCourses(state, courses) {
    Vue.set(state, 'selectedCourses', {});
    courses.forEach(course => {
      Vue.set(state.selectedCourses, course.id, course);
    });
  },
  setLastRefresh(state) {
    state.lastRefresh = new Date();
  },
  updateCourse(state, newCourse) {
    Vue.set(state.nextCourses, newCourse.id, newCourse);
  },
  setCourseConnectionLocationActivated(state, activated) {
    state.courseConnectionLocationActivated = activated;
  },
  setSelectedStationFilter(state, station) {
    state.selectedStationFilter = station;
  },
};

export const actions = {
  getCourses({ commit }) {
    return getCourses().then(response => {
      commit('setCourses', response.data);
    });
  },
  getStationsCandidates({ commit }) {
    return getStations().then(response => {
      commit('setStations', response.data);
    });
  },
  getCourseConnectionsLocations({ commit }) {
    return getCourseConnectionsLocations().then(response => {
      commit('setCourseConnectionsLocations', response.data);
    });
  },
  deactivateCourseConnectionLocation({ commit, dispatch }, locationId) {
    return deactivateCourseConnectionLocation(locationId).then(response => {
      commit('deactivateStation', locationId);
      dispatch('getNextCourses');
    });
  },
  activateStation({ commit, dispatch }, sloid) {
    return activateStation(sloid).then(response => {
      commit('activateStation', response.data);
      dispatch('getNextCourses');
    });
  },
  getNextCourses({ commit }) {
    return getNextCourses().then(response => {
      commit('setNextCourses', response.data);
      commit('setLastRefresh');
    });
  },
  setSelectedCourses({ commit }, courses) {
    commit('setSelectedCourses', courses);
  },
  blockSelectedCourses({ state }) {
    Object.values(state.selectedCourses).forEach(course => {
      return blockCourse(course.id);
    });
  },
  releaseSelectedCourses({ state }) {
    Object.values(state.selectedCourses).forEach(course => {
      return releaseCourse(course.id);
    });
  },
  delayDeparture({ state }, delay) {
    Object.values(state.selectedCourses).forEach(course => {
      return delayDeparture(course.id, delay);
    });
  },
  updateCourse({ commit }, newCourse) {
    commit('updateCourse', newCourse);
  },
  setCourseConnectionLocationActivated({ commit }, activated) {
    commit('setCourseConnectionLocationActivated', activated);
  },
  setSelectedStationFilter({ commit }, station) {
    commit('setSelectedStationFilter', station);
  },
  resetSelectedStationFilter({ commit }) {
    commit('setSelectedStationFilter', null);
  },
};

export const getters = {
  getLines: state => {
    return Object.values(state.index)
      .map(course => course.line)
      .filter((line, index, self) => line && self.indexOf(line) === index);
  },
  getRegions: state => {
    return Object.values(state.index)
      .map(course => course.region)
      .filter(
        (region, index, self) => region && self.indexOf(region) === index
      );
  },
  anyNextCourseBlocked: state => {
    return Object.values(state.nextCourses).find(
      course => course.connection.is_blocked
    );
  },
  anyNextCourseDelayed: state => {
    return Object.values(state.nextCourses).find(
      course => course.connection.delay_minutes !== 0
    );
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
